import { useState } from 'react';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { logger } from '../../lib/logger';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@pantry/ui';
import { cn } from '@pantry/ui';
import {
  useItem,
  useItemInstances,
  useCreateItemInstance,
  useUpdateItemInstance,
  useUpdateInstanceDetails,
  useUpdateItem,
  useDeleteItem,
  useStorageLocations,
  type ItemInstance,
  WASTE_REASON_LABELS,
  resolveExpiryThreshold,
  getExpiryStatus as getExpiryStatusShared,
} from '@pantry/api-client';
import { useSelectedHousehold } from '../../contexts/HouseholdContext';
import {
  UNIT_DISPLAY_LABELS,
  type Product,
  type InstanceFormValues,
  getDaysUntilExpiry,
  parseLocalDate,
  DEFAULT_EXPIRY_THRESHOLD_DAYS,
} from '@pantry/shared';
import { useResolveProductName } from '../../hooks/useProductLanguage';
import { ItemFormDialog } from '../../components/items/item-form-dialog';
import { UnifiedItemForm } from '../../components/items/unified-form';
import { WasteReasonDialog } from '../../components/items/waste-reason-dialog';
import { ConsumeReasonDialog } from '../../components/items/consume-reason-dialog';
import { RemoveItemDialog } from '../../components/items/remove-item-dialog';
import { SetTargetDialog } from '../../components/inventory/SetTargetDialog';
import { TranslationIndicator } from '../../components/products/TranslationIndicator';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ArchiveBoxIcon,
  HomeIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

export const Route = createFileRoute('/_app/items_/$itemId')({
  component: ItemDetailPage,
});

function ItemDetailPage() {
  const { t } = useLingui();

  const getStorageTypeLabel = (type: string): string => {
    switch (type) {
      case 'fridge':
        return t`Fridge`;
      case 'freezer':
        return t`Freezer`;
      case 'pantry':
        return t`Pantry`;
      case 'other':
        return t`Other`;
      default:
        return type;
    }
  };

  const { itemId } = Route.useParams();
  const navigate = useNavigate();
  const { selectedHousehold, isLoading: householdLoading } = useSelectedHousehold();

  const householdId = selectedHousehold?.id || '';

  const [showEditForm, setShowEditForm] = useState(false);
  const [showAddInstance, setShowAddInstance] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showTargetDialog, setShowTargetDialog] = useState(false);

  const { data: item, isLoading: itemLoading, error: itemError } = useItem(householdId, itemId);

  const {
    data: instances,
    isLoading: instancesLoading,
    error: instancesError,
  } = useItemInstances(householdId, itemId);

  const { data: storageLocations } = useStorageLocations(householdId);
  const deleteItem = useDeleteItem();
  const resolveProductName = useResolveProductName();

  const isLoading = householdLoading || itemLoading;

  // Find storage location name
  const storageLocation = storageLocations?.find((loc) => loc.id === item?.storageLocationId);

  // Show empty state if no household selected
  if (!householdLoading && !selectedHousehold) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="text-center max-w-md animate-in">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
            <HomeIcon className="h-10 w-10 text-primary" />
          </div>
          <h1 className="font-display text-3xl font-semibold text-foreground mb-3">
            <Trans>No Household Selected</Trans>
          </h1>
          <p className="text-muted-foreground mb-6">
            <Trans>Create or select a household to view item details.</Trans>
          </p>
          <Button asChild size="lg">
            <Link to="/households">
              <PlusIcon className="h-5 w-5 mr-2" />
              <Trans>Manage Households</Trans>
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-8 pb-8">
        {/* Breadcrumb skeleton */}
        <div className="h-5 w-24 animate-pulse rounded bg-muted" />

        {/* Header skeleton */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between animate-in">
          <div className="flex gap-4">
            <div className="h-20 w-20 animate-pulse rounded-2xl bg-muted" />
            <div className="space-y-2">
              <div className="h-8 w-48 animate-pulse rounded bg-muted" />
              <div className="h-4 w-32 animate-pulse rounded bg-muted" />
              <div className="h-4 w-40 animate-pulse rounded bg-muted" />
            </div>
          </div>
        </div>

        {/* Cards skeleton */}
        <Card className="animate-in" style={{ animationDelay: '100ms' }}>
          <CardContent className="p-6">
            <div className="h-6 w-40 animate-pulse rounded bg-muted mb-4" />
            <div className="grid gap-4 sm:grid-cols-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                  <div className="h-5 w-36 animate-pulse rounded bg-muted" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (itemError) {
    return (
      <div className="space-y-6 animate-in">
        <Link
          to="/items"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <Trans>Back to Items</Trans>
        </Link>

        <div className="min-h-[40vh] flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center mb-4">
              <ExclamationTriangleIcon className="h-8 w-8 text-destructive" />
            </div>
            <h1 className="font-display text-2xl font-semibold text-foreground mb-2">
              <Trans>Item Not Found</Trans>
            </h1>
            <p className="text-muted-foreground mb-6">
              {itemError.message || t`This item doesn't exist or you don't have access to it.`}
            </p>
            <Button asChild variant="outline">
              <Link to="/items">
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                <Trans>Back to Inventory</Trans>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!item) {
    return null;
  }

  const handleRemoveSuccess = () => {
    // Navigate back to inventory after all instances are processed
    navigate({ to: '/items' });
  };

  // Display custom name if set, fall back to localized product name, then "Unnamed Item"
  const itemName =
    item.customName ||
    (item.product ? resolveProductName(item.product as Product) : null) ||
    t`Unnamed Item`;
  const itemBrand = item.product?.brand;
  const itemImage = item.product?.imageUrl;

  // Separate instances by status
  const activeInstances = instances?.filter((i) => i.status === 'active') || [];
  const consumedInstances = instances?.filter((i) => i.status === 'consumed') || [];
  const wastedInstances = instances?.filter((i) => i.status === 'wasted') || [];

  // Calculate expiry stats for the header
  const earliestExpiry = activeInstances
    .filter((i) => i.expiryDate)
    .sort((a, b) => (a.expiryDate || '').localeCompare(b.expiryDate || ''))[0];
  const earliestExpiryDays = earliestExpiry?.expiryDate
    ? getDaysUntilExpiry(earliestExpiry.expiryDate)
    : null;

  // Resolve the effective expiry threshold for this item
  const effectiveThreshold = resolveExpiryThreshold(
    item,
    storageLocation ?? null,
    selectedHousehold ?? null
  );

  return (
    <div className="space-y-8 pb-8">
      {/* Breadcrumb */}
      <Link
        to="/items"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors animate-in"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        <Trans>Back to Inventory</Trans>
      </Link>

      {/* Item header */}
      <section
        className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between animate-in"
        style={{ animationDelay: '50ms' }}
      >
        <div className="flex gap-5">
          {/* Product image */}
          <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-2xl overflow-hidden bg-muted/50 ring-1 ring-border/50">
            {itemImage ? (
              <img src={itemImage} alt={itemName} className="h-full w-full object-cover" />
            ) : (
              <ArchiveBoxIcon className="h-10 w-10 text-muted-foreground" />
            )}
          </div>
          <div className="space-y-1">
            <h1 className="font-display text-2xl sm:text-3xl font-semibold text-foreground">
              {itemName}
            </h1>
            {itemBrand && <p className="text-muted-foreground">{itemBrand}</p>}
            {/* Translation indicator when product is using a fallback language */}
            {item.product && (
              <TranslationIndicator
                product={item.product as Product}
                currentName={itemName}
                variant="inline"
              />
            )}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                <ArchiveBoxIcon className="h-4 w-4" />
                {storageLocation?.name || t`No location`}
              </span>
              <span className="text-muted-foreground/50">•</span>
              <span className="text-sm font-medium text-foreground">
                {activeInstances.reduce((sum, i) => sum + i.quantity, 0)}{' '}
                {UNIT_DISPLAY_LABELS[item.unit]} active
              </span>
              {earliestExpiryDays !== null && (
                <>
                  <span className="text-muted-foreground/50">•</span>
                  <ExpiryBadge days={earliestExpiryDays} thresholdDays={effectiveThreshold} />
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowEditForm(true)} className="h-10">
            <PencilIcon className="h-4 w-4 mr-2" />
            <Trans>Edit Details</Trans>
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowRemoveDialog(true)}
            disabled={activeInstances.length === 0}
            className="h-10 text-destructive hover:text-destructive hover:bg-destructive/10 hover:border-destructive/30"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            <Trans>Remove</Trans>
          </Button>
        </div>
      </section>

      {/* Item info card */}
      <Card className="overflow-hidden relative animate-in" style={{ animationDelay: '100ms' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
        <CardContent className="p-6 relative">
          <h2 className="font-heading font-semibold text-lg text-foreground mb-4">
            <Trans>Item Information</Trans>
          </h2>
          <dl className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt className="text-sm text-muted-foreground mb-1">
                <Trans>Product</Trans>
              </dt>
              <dd className="font-medium text-foreground">
                {itemName}
                {itemBrand && (
                  <span className="font-normal text-muted-foreground">
                    {' '}
                    <Trans>by {itemBrand}</Trans>
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground mb-1">
                <Trans>Active Quantity</Trans>
              </dt>
              <dd className="font-medium text-foreground">
                {activeInstances.reduce((sum, i) => sum + i.quantity, 0)}{' '}
                {UNIT_DISPLAY_LABELS[item.unit]}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground mb-1">
                <Trans>Storage Location</Trans>
              </dt>
              <dd className="font-medium text-foreground">
                {storageLocation?.name || t`Not specified`}
                {storageLocation?.type && (
                  <span className="font-normal text-muted-foreground">
                    {' '}
                    ({getStorageTypeLabel(storageLocation.type)})
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground mb-1">
                <Trans>Stock Batches</Trans>
              </dt>
              <dd className="font-medium text-foreground">
                <Plural value={activeInstances.length} one="# batch" other="# batches" />
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground mb-1">
                <Trans>Stock Target</Trans>
              </dt>
              <dd className="font-medium text-foreground">
                {item.targetQuantity != null ? (
                  <span className="inline-flex items-center gap-2">
                    {item.targetQuantity}{' '}
                    {item.targetUnit
                      ? UNIT_DISPLAY_LABELS[item.targetUnit as keyof typeof UNIT_DISPLAY_LABELS] ||
                        item.targetUnit
                      : UNIT_DISPLAY_LABELS[item.unit]}
                    <button
                      type="button"
                      onClick={() => setShowTargetDialog(true)}
                      className="text-muted-foreground hover:text-primary transition-colors"
                      aria-label={t`Edit stock target`}
                    >
                      <PencilIcon className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowTargetDialog(true)}
                    className="text-primary hover:text-primary/80 transition-colors text-sm"
                  >
                    <Trans context="action_button">Set target</Trans>
                  </button>
                )}
              </dd>
            </div>
            {item.notes && (
              <div className="sm:col-span-2 lg:col-span-4">
                <dt className="text-sm text-muted-foreground mb-1">
                  <Trans>Notes</Trans>
                </dt>
                <dd className="text-foreground">{item.notes}</dd>
              </div>
            )}
          </dl>

          {/* Stock progress bar (only when target is set) */}
          {item.targetQuantity != null && item.targetQuantity > 0 && (
            <StockProgressBar
              current={activeInstances.reduce((sum, i) => sum + i.quantity, 0)}
              target={item.targetQuantity}
              unit={
                item.targetUnit
                  ? UNIT_DISPLAY_LABELS[item.targetUnit as keyof typeof UNIT_DISPLAY_LABELS] ||
                    item.targetUnit
                  : UNIT_DISPLAY_LABELS[item.unit]
              }
            />
          )}

          {/* Expiry Threshold Override */}
          <ExpiryThresholdField
            item={item}
            storageLocation={storageLocation ?? null}
            household={selectedHousehold ?? null}
            householdId={householdId}
          />
        </CardContent>
      </Card>

      {/* Active instances */}
      <Card className="animate-in" style={{ animationDelay: '150ms' }}>
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-4">
            <div>
              <h2 className="font-heading font-semibold text-lg text-foreground">
                <Trans>Active Stock</Trans>
              </h2>
              <p className="text-sm text-muted-foreground">
                <Trans>Currently in your pantry. Each batch can have different expiry dates.</Trans>
              </p>
            </div>
            <Button onClick={() => setShowAddInstance(true)} className="shrink-0">
              <PlusIcon className="h-4 w-4 mr-2" />
              <Trans>Add Stock</Trans>
            </Button>
          </div>

          {instancesLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-muted/30">
                  <div className="h-12 w-12 animate-pulse rounded-xl bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-32 animate-pulse rounded bg-muted" />
                    <div className="h-3 w-48 animate-pulse rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          ) : instancesError ? (
            <div className="py-8 text-center">
              <div className="mx-auto w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center mb-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-destructive" />
              </div>
              <p className="text-destructive font-medium">
                <Trans>Failed to load stock</Trans>
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                <Trans>Please try refreshing the page</Trans>
              </p>
            </div>
          ) : activeInstances.length > 0 ? (
            <div className="space-y-3">
              {activeInstances.map((instance, index) => (
                <InstanceRow
                  key={instance.id}
                  instance={instance}
                  householdId={householdId}
                  itemId={itemId}
                  unit={item?.unit}
                  index={index}
                  thresholdDays={effectiveThreshold}
                />
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <div className="mx-auto w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-3">
                <ArchiveBoxIcon className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="font-medium text-foreground">
                <Trans>No active stock</Trans>
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                <Trans>Add stock to start tracking expiry dates</Trans>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* History (consumed/wasted) */}
      {(consumedInstances.length > 0 || wastedInstances.length > 0) && (
        <Card className="animate-in" style={{ animationDelay: '200ms' }}>
          <CardContent className="p-6">
            <div className="mb-4">
              <h2 className="font-heading font-semibold text-lg text-foreground">
                <Trans>History</Trans>
              </h2>
              <p className="text-sm text-muted-foreground">
                <Trans>Previously consumed or wasted</Trans>
              </p>
            </div>
            <div className="space-y-3">
              {[...consumedInstances, ...wastedInstances]
                .sort(
                  (a, b) =>
                    new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()
                )
                .slice(0, 10)
                .map((instance, index) => (
                  <InstanceRow
                    key={instance.id}
                    instance={instance}
                    householdId={householdId}
                    itemId={itemId}
                    unit={item?.unit}
                    isHistory
                    index={index}
                    thresholdDays={effectiveThreshold}
                  />
                ))}
              {consumedInstances.length + wastedInstances.length > 10 && (
                <p className="text-sm text-muted-foreground text-center py-2">
                  <Trans>
                    Showing 10 of {consumedInstances.length + wastedInstances.length} entries
                  </Trans>
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit dialog */}
      {showEditForm && (
        <ItemFormDialog
          householdId={householdId}
          item={item}
          onClose={() => setShowEditForm(false)}
        />
      )}

      {/* Add instance dialog */}
      <AddInstanceDialog
        open={showAddInstance}
        onOpenChange={setShowAddInstance}
        householdId={householdId}
        itemId={itemId}
      />

      {/* Remove item dialog */}
      <RemoveItemDialog
        open={showRemoveDialog}
        onOpenChange={setShowRemoveDialog}
        householdId={householdId}
        itemId={itemId}
        itemName={itemName}
        unit={item.unit}
        activeInstances={activeInstances}
        onSuccess={handleRemoveSuccess}
      />

      <SetTargetDialog
        open={showTargetDialog}
        onOpenChange={setShowTargetDialog}
        mode="item"
        entityId={itemId}
        entityName={itemName}
        householdId={householdId}
        currentQuantity={item.targetQuantity}
        currentUnit={item.targetUnit}
      />
    </div>
  );
}

/**
 * Expiry badge component for header
 */
function ExpiryBadge({ days, thresholdDays }: { days: number; thresholdDays: number }) {
  const { t } = useLingui();
  const status = getExpiryStatusShared(days, thresholdDays);

  // i18n: "d" abbreviates "days", e.g. "3d left" = "3 days left"
  const getExpiryText = (days: number): string => {
    if (days < 0) {
      const absDays = Math.abs(days);
      return t`Expired ${absDays}d ago`;
    }
    if (days === 0) return t`Expires today`;
    if (days === 1) return t`Expires tomorrow`;
    return t`${days}d left`;
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-sm font-medium px-2 py-0.5 rounded-full',
        status === 'expired' && 'bg-expired/10 text-expired',
        status === 'expiring-today' && 'bg-expiring-today/10 text-expiring-today',
        status === 'expiring-soon' && 'bg-expiring-soon/10 text-expiring-soon',
        status === 'fresh' && 'bg-fresh/10 text-fresh'
      )}
    >
      <ClockIcon className="h-3.5 w-3.5" />
      {getExpiryText(days)}
    </span>
  );
}

/**
 * Instance row component with quick actions.
 */
function InstanceRow({
  instance,
  householdId,
  itemId,
  unit,
  isHistory = false,
  index = 0,
  thresholdDays,
}: {
  instance: ItemInstance;
  householdId: string;
  itemId: string;
  unit?: import('@pantry/api-client').ItemUnit;
  isHistory?: boolean;
  index?: number;
  thresholdDays: number;
}) {
  const { t } = useLingui();
  const [showWasteDialog, setShowWasteDialog] = useState(false);
  const [showConsumeDialog, setShowConsumeDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const updateInstance = useUpdateItemInstance();

  const expiryDate = instance.expiryDate;
  const daysUntilExpiry = expiryDate ? getDaysUntilExpiry(expiryDate) : null;
  const expiryStatus = getExpiryStatusShared(daysUntilExpiry, thresholdDays);

  // Determine border color based on status
  const getBorderColor = () => {
    if (instance.status === 'consumed') return 'border-l-fresh';
    if (instance.status === 'wasted') return 'border-l-destructive';
    if (expiryStatus === 'expired') return 'border-l-expired';
    if (expiryStatus === 'expiring-today') return 'border-l-expiring-today';
    if (expiryStatus === 'expiring-soon') return 'border-l-expiring-soon';
    return 'border-l-fresh';
  };

  return (
    <>
      <div
        className={cn(
          'flex items-center gap-4 p-4 rounded-xl border-l-4 transition-colors',
          isHistory ? 'bg-muted/20' : 'bg-muted/30 hover:bg-muted/50',
          getBorderColor(),
          'animate-in'
        )}
        style={{ animationDelay: `${index * 50}ms` }}
      >
        {/* Status icon */}
        <div
          className={cn(
            'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl',
            instance.status === 'consumed' && 'bg-fresh/10',
            instance.status === 'wasted' && 'bg-destructive/10',
            instance.status === 'active' && expiryStatus === 'expired' && 'bg-expired/10',
            instance.status === 'active' &&
              expiryStatus === 'expiring-today' &&
              'bg-expiring-today/10',
            instance.status === 'active' &&
              expiryStatus === 'expiring-soon' &&
              'bg-expiring-soon/10',
            instance.status === 'active' &&
              (expiryStatus === 'fresh' || expiryStatus === 'none') &&
              'bg-fresh/10'
          )}
        >
          {instance.status === 'consumed' ? (
            <CheckIcon className="h-6 w-6 text-fresh" />
          ) : instance.status === 'wasted' ? (
            <XMarkIcon className="h-6 w-6 text-destructive" />
          ) : expiryStatus === 'expired' ? (
            <ExclamationTriangleIcon className="h-6 w-6 text-expired" />
          ) : expiryStatus === 'expiring-today' ? (
            <ExclamationTriangleIcon className="h-6 w-6 text-expiring-today" />
          ) : expiryStatus === 'expiring-soon' ? (
            <ClockIcon className="h-6 w-6 text-expiring-soon" />
          ) : (
            <CheckIcon className="h-6 w-6 text-fresh" />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            {/* Quantity with unit */}
            <span className="font-medium text-foreground">
              Qty: {instance.quantity} {unit ? UNIT_DISPLAY_LABELS[unit] : ''}
            </span>

            {/* Expiry badge for active */}
            {instance.status === 'active' && expiryDate && (
              <span
                className={cn(
                  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                  expiryStatus === 'expired' && 'bg-expired/10 text-expired',
                  expiryStatus === 'expiring-today' && 'bg-expiring-today/10 text-expiring-today',
                  expiryStatus === 'expiring-soon' && 'bg-expiring-soon/10 text-expiring-soon',
                  expiryStatus === 'fresh' && 'bg-fresh/10 text-fresh'
                )}
              >
                {daysUntilExpiry !== null && daysUntilExpiry < 0
                  ? `Expired ${Math.abs(daysUntilExpiry)}d ago`
                  : daysUntilExpiry === 0
                    ? 'Expires today'
                    : daysUntilExpiry === 1
                      ? 'Tomorrow'
                      : `${daysUntilExpiry}d left`}
              </span>
            )}

            {/* Status badge for history */}
            {isHistory && (
              <span
                className={cn(
                  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                  instance.status === 'consumed' && 'bg-fresh/10 text-fresh',
                  instance.status === 'wasted' && 'bg-destructive/10 text-destructive'
                )}
              >
                {instance.status === 'consumed' ? <Trans>Consumed</Trans> : <Trans>Wasted</Trans>}
              </span>
            )}

            {/* Waste reason */}
            {instance.status === 'wasted' && instance.wasteReason && (
              <span className="text-xs text-muted-foreground">
                ({WASTE_REASON_LABELS[instance.wasteReason]})
              </span>
            )}
          </div>

          <div className="text-sm text-muted-foreground">
            {instance.purchaseDate && (
              <span>Purchased {format(parseLocalDate(instance.purchaseDate), 'MMM d, yyyy')}</span>
            )}
            {instance.purchaseDate && instance.expiryDate && <span className="mx-1">•</span>}
            {instance.expiryDate && (
              <span>Expires {format(parseLocalDate(instance.expiryDate), 'MMM d, yyyy')}</span>
            )}
            {!instance.purchaseDate && !instance.expiryDate && (
              <span className="italic">
                <Trans>No dates set</Trans>
              </span>
            )}
          </div>
        </div>

        {/* Actions (only for active instances) */}
        {instance.status === 'active' && (
          <div className="flex gap-1 flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowEditDialog(true)}
              title={t`Edit quantity/dates`}
              className="h-9 w-9 p-0 hover:bg-muted"
            >
              <PencilIcon className="h-4 w-4 text-muted-foreground" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowConsumeDialog(true)}
              disabled={updateInstance.isPending}
              title={t`Mark as consumed`}
              className="h-9 w-9 p-0 text-fresh hover:text-fresh hover:bg-fresh/10"
            >
              <CheckIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowWasteDialog(true)}
              disabled={updateInstance.isPending}
              title={t`Mark as wasted`}
              className="h-9 w-9 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Dialogs */}
      {showWasteDialog && (
        <WasteReasonDialog
          householdId={householdId}
          itemId={itemId}
          instanceId={instance.id}
          instanceQuantity={instance.quantity}
          unit={unit}
          onClose={() => setShowWasteDialog(false)}
        />
      )}

      {showConsumeDialog && (
        <ConsumeReasonDialog
          householdId={householdId}
          itemId={itemId}
          instanceId={instance.id}
          instanceQuantity={instance.quantity}
          unit={unit}
          onClose={() => setShowConsumeDialog(false)}
        />
      )}

      <EditInstanceDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        householdId={householdId}
        itemId={itemId}
        instance={instance}
      />
    </>
  );
}

/**
 * Inline expiry threshold override field for item detail.
 */
function ExpiryThresholdField({
  item,
  storageLocation,
  household,
  householdId,
}: {
  item: { id: string; expiryThresholdDays: number | null };
  storageLocation: { expiryThresholdDays: number | null } | null;
  household: { expiryThresholdDays: number } | null;
  householdId: string;
}) {
  const { t } = useLingui();
  const updateItem = useUpdateItem();
  const [localValue, setLocalValue] = useState<string>(
    item.expiryThresholdDays != null ? String(item.expiryThresholdDays) : ''
  );

  const effectiveThreshold = resolveExpiryThreshold(item, storageLocation, household);

  // Determine the source of the effective threshold
  const thresholdSource =
    item.expiryThresholdDays != null
      ? t`item`
      : storageLocation?.expiryThresholdDays != null
        ? t`location`
        : household?.expiryThresholdDays != null
          ? t`household`
          : t`default`;

  const handleSave = (value: string) => {
    const parsed = parseInt(value, 10);
    const clamped = isNaN(parsed) || parsed < 1 ? null : Math.min(parsed, 365);
    const newValue = value === '' ? null : clamped;

    if (newValue === null) {
      setLocalValue('');
    } else if (newValue !== parsed) {
      setLocalValue(String(newValue));
    }

    if (newValue === item.expiryThresholdDays) return;

    updateItem.mutate({
      householdId,
      id: item.id,
      data: { expiryThresholdDays: newValue },
    });
  };

  const handleClear = () => {
    setLocalValue('');
    handleSave('');
  };

  return (
    <div className="mt-6 pt-6 border-t border-border/50">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <label htmlFor="item-expiry-threshold" className="text-sm font-medium text-foreground">
            <Trans>Expiry Alert Threshold</Trans>
          </label>
          <p className="text-xs text-muted-foreground mt-0.5">
            <Trans>
              Currently using: {effectiveThreshold} days (from {thresholdSource})
            </Trans>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="item-expiry-threshold"
            type="number"
            inputMode="numeric"
            min={1}
            max={365}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={(e) => handleSave(e.target.value)}
            placeholder={String(
              storageLocation?.expiryThresholdDays ??
                household?.expiryThresholdDays ??
                DEFAULT_EXPIRY_THRESHOLD_DAYS
            )}
            aria-label={t`Expiry alert threshold in days`}
            className="w-20 h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <span className="text-sm text-muted-foreground">
            <Trans>days</Trans>
          </span>
          {item.expiryThresholdDays != null && (
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-primary hover:underline"
              aria-label={t`Clear threshold override`}
            >
              <Trans>Clear</Trans>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Dialog for adding a new item instance.
 */
function AddInstanceDialog({
  open,
  onOpenChange,
  householdId,
  itemId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  householdId: string;
  itemId: string;
}) {
  const { t } = useLingui();
  const [error, setError] = useState<string | null>(null);
  const createInstance = useCreateItemInstance();

  const handleSubmit = async (values: InstanceFormValues) => {
    setError(null);
    try {
      await createInstance.mutateAsync({
        householdId,
        itemId,
        data: {
          quantity: values.quantity > 0 ? values.quantity : 1,
          purchaseDate: values.purchaseDate,
          expiryDate: values.expiryDate,
          notes: values.notes,
        },
      });
      onOpenChange(false);
    } catch (err) {
      logger.error('Failed to add instance', err, { householdId, itemId });
      setError(err instanceof Error ? err.message : t`Failed to add stock`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            <Trans>Add Stock</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>Add a new batch of this item to your inventory with its expiry date.</Trans>
          </DialogDescription>
        </DialogHeader>

        <UnifiedItemForm
          mode="create-instance"
          householdId={householdId}
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
          isPending={createInstance.isPending}
          error={error}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Dialog for editing an existing item instance (quantity, dates).
 */
function EditInstanceDialog({
  open,
  onOpenChange,
  householdId,
  itemId,
  instance,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  householdId: string;
  itemId: string;
  instance: ItemInstance;
}) {
  const { t } = useLingui();
  const [error, setError] = useState<string | null>(null);
  const updateInstanceDetails = useUpdateInstanceDetails();

  const handleSubmit = async (values: InstanceFormValues) => {
    setError(null);
    try {
      await updateInstanceDetails.mutateAsync({
        householdId,
        itemId,
        instanceId: instance.id,
        data: {
          quantity: values.quantity > 0 ? values.quantity : 1,
          purchaseDate: values.purchaseDate,
          expiryDate: values.expiryDate,
          notes: values.notes,
        },
      });
      onOpenChange(false);
    } catch (err) {
      logger.error('Failed to update instance', err, { householdId, itemId });
      setError(err instanceof Error ? err.message : t`Failed to update instance`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            <Trans>Edit Stock Batch</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>Update the quantity or dates for this batch.</Trans>
          </DialogDescription>
        </DialogHeader>

        <UnifiedItemForm
          mode="edit-instance"
          instance={instance}
          householdId={householdId}
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
          isPending={updateInstanceDetails.isPending}
          error={error}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Visual stock progress bar showing current vs target quantity.
 */
function StockProgressBar({
  current,
  target,
  unit,
}: {
  current: number;
  target: number;
  unit: string;
}) {
  const percent = Math.min((current / target) * 100, 100);
  const isStocked = current >= target;
  const isLow = !isStocked && current > 0;
  const isOut = current === 0;

  const barColor = isStocked ? 'bg-fresh' : isLow ? 'bg-expiring-soon' : 'bg-destructive';

  const textColor = isStocked ? 'text-fresh' : isLow ? 'text-expiring-soon' : 'text-destructive';

  return (
    <div className="mt-6 space-y-2">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-foreground">
          <Trans context="stock_status">Stock Level</Trans>
        </span>
        <span className={cn('text-sm font-medium', textColor)}>
          {current} / {target} {unit}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-500', barColor)}
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className={cn('text-xs', textColor)}>
        {isOut ? (
          <Trans context="stock_status">Out of stock</Trans>
        ) : isLow ? (
          <Trans context="stock_status">
            Below target — {target - current} {unit} needed
          </Trans>
        ) : (
          <Trans context="stock_status">Target met</Trans>
        )}
      </p>
    </div>
  );
}
